import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormField from '../../components/FormField';
import QRCode from '../../components/QRCode';
import { useSchemas } from '../../api/hooks';
import { apiFetch } from '../../api/client';

/**
 * Phase 7-C — Presentation Definition authoring surface.
 *
 * Replaces the legacy single-credential flat-attribute builder with a true
 * DIF Presentation Exchange v2 form. State shape *is* the canonical PD
 * JSON; a "Show JSON" toggle previews the wire body byte-for-byte.
 *
 * Architecture: 01_architecture.md §4.8.
 * Wire spec:    02_api_spec.md §2 (POST), §11 (PD), §12 (InputDescriptor),
 *               §16 (SubmissionRequirement), §19 (FormatDescriptor).
 */

type FormatBucket = 'ldp_vc' | 'jwt_vc' | 'vc+sd-jwt';
const FORMAT_BUCKETS: FormatBucket[] = ['ldp_vc', 'jwt_vc', 'vc+sd-jwt'];

interface BuilderField {
  path: string;
  filter?: string; // raw JSON-Schema filter (parsed on save)
  predicate?: 'required' | 'preferred' | '';
  optional?: boolean;
  purpose?: string;
}

interface BuilderDescriptor {
  id: string;
  name?: string;
  purpose?: string;
  group?: string; // comma-separated group keys
  schemaId?: string; // selected from useSchemas()
  formats: FormatBucket[];
  fields: BuilderField[];
}

interface BuilderSR {
  name?: string;
  rule: 'all' | 'pick';
  count?: string; // string-typed for input handling
  min?: string;
  max?: string;
  from?: string;
}

const newDescriptor = (idx: number): BuilderDescriptor => ({
  id: `desc_${idx}`,
  name: '',
  purpose: '',
  group: '',
  schemaId: '',
  formats: ['ldp_vc'],
  fields: [{ path: '$.credentialSubject.', optional: false, predicate: '' }],
});

// UUIDv4 — used for the optional `Idempotency-Key` header (closes #38 docs).
function uuidv4(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return (crypto as Crypto).randomUUID();
  // RFC 4122 v4 fallback — only used if running in a context without crypto.randomUUID.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function compilePD(
  pdMeta: { name: string; purpose: string; verifierDid: string },
  descriptors: BuilderDescriptor[],
  srs: BuilderSR[],
): { definition: Record<string, unknown>; warnings: string[] } {
  const warnings: string[] = [];

  // Build descriptor list.
  const input_descriptors = descriptors.map((d) => {
    const format: Record<string, unknown> = {};
    for (const b of d.formats) {
      format[b] = b === 'ldp_vc' ? { proof_type: ['Ed25519Signature2020', 'BbsBlsSignature2020'] } : {};
    }
    const fields = d.fields
      .filter((f) => f.path && f.path !== '$.credentialSubject.')
      .map((f) => {
        const out: Record<string, unknown> = { path: [f.path] };
        if (f.purpose) out.purpose = f.purpose;
        if (f.optional) out.optional = true;
        if (f.predicate) out.predicate = f.predicate;
        if (f.filter) {
          try {
            out.filter = JSON.parse(f.filter);
          } catch {
            warnings.push(`descriptor "${d.id}": filter JSON parse error — dropped`);
          }
        }
        return out;
      });
    const constraints: Record<string, unknown> = {};
    if (fields.length > 0) constraints.fields = fields;
    if (d.schemaId) constraints.schema_id = d.schemaId;

    const out: Record<string, unknown> = { id: d.id };
    if (d.name) out.name = d.name;
    if (d.purpose) out.purpose = d.purpose;
    if (d.group) out.group = d.group.split(',').map((s) => s.trim()).filter(Boolean);
    if (Object.keys(format).length > 0) out.format = format;
    if (Object.keys(constraints).length > 0) out.constraints = constraints;
    return out;
  });

  const submission_requirements = srs
    .filter((sr) => sr.rule)
    .map((sr) => {
      const out: Record<string, unknown> = { rule: sr.rule };
      if (sr.name) out.name = sr.name;
      if (sr.from) out.from = sr.from.trim();
      if (sr.rule === 'pick') {
        if (sr.count) out.count = Number(sr.count);
        if (sr.min) out.min = Number(sr.min);
        if (sr.max) out.max = Number(sr.max);
      }
      return out;
    });

  const definition: Record<string, unknown> = {
    id: `pd_${uuidv4().replace(/-/g, '').slice(0, 26).toUpperCase()}`,
    purpose: pdMeta.purpose || undefined,
    input_descriptors,
  };
  if (submission_requirements.length > 0) definition.submission_requirements = submission_requirements;

  return { definition, warnings };
}

export default function ProofRequestBuilder() {
  const { t } = useTranslation('verifier');
  const { data: schemas } = useSchemas();

  // PD metadata
  const [pdName, setPdName] = useState('');
  const [pdPurpose, setPdPurpose] = useState('');
  const [verifierDid, setVerifierDid] = useState('did:ida:verifier:trustverify-001');

  // Descriptors & SRs
  const [descriptors, setDescriptors] = useState<BuilderDescriptor[]>([newDescriptor(0)]);
  const [srs, setSrs] = useState<BuilderSR[]>([]);

  // UI state
  const [showJson, setShowJson] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedRecord, setSavedRecord] = useState<{ id: string; url: string; qrPayload?: string } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const compiled = useMemo(
    () => compilePD({ name: pdName, purpose: pdPurpose, verifierDid }, descriptors, srs),
    [pdName, pdPurpose, verifierDid, descriptors, srs],
  );

  const updateDescriptor = (idx: number, patch: Partial<BuilderDescriptor>) =>
    setDescriptors((ds) => ds.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  const updateField = (descIdx: number, fieldIdx: number, patch: Partial<BuilderField>) =>
    setDescriptors((ds) =>
      ds.map((d, i) =>
        i === descIdx ? { ...d, fields: d.fields.map((f, j) => (j === fieldIdx ? { ...f, ...patch } : f)) } : d,
      ),
    );

  const addDescriptor = () => setDescriptors((ds) => [...ds, newDescriptor(ds.length)]);
  const removeDescriptor = (idx: number) => setDescriptors((ds) => ds.filter((_, i) => i !== idx));
  const addField = (descIdx: number) =>
    setDescriptors((ds) =>
      ds.map((d, i) => (i === descIdx ? { ...d, fields: [...d.fields, { path: '$.credentialSubject.', optional: false, predicate: '' }] } : d)),
    );
  const removeField = (descIdx: number, fieldIdx: number) =>
    setDescriptors((ds) => ds.map((d, i) => (i === descIdx ? { ...d, fields: d.fields.filter((_, j) => j !== fieldIdx) } : d)));

  const toggleFormat = (descIdx: number, bucket: FormatBucket) =>
    setDescriptors((ds) =>
      ds.map((d, i) =>
        i === descIdx
          ? { ...d, formats: d.formats.includes(bucket) ? d.formats.filter((b) => b !== bucket) : [...d.formats, bucket] }
          : d,
      ),
    );

  const addSR = () => setSrs((s) => [...s, { rule: 'pick', from: '' }]);
  const updateSR = (idx: number, patch: Partial<BuilderSR>) =>
    setSrs((s) => s.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const removeSR = (idx: number) => setSrs((s) => s.filter((_, i) => i !== idx));

  const handleSave = async () => {
    setSaveError(null);
    setSaving(true);
    try {
      const body = {
        name: pdName || undefined,
        verifierDid,
        definition: compiled.definition,
        validity: 'PT1H',
      };
      const idempotencyKey = uuidv4();
      const out = await apiFetch<{ id: string; url?: string; qrPayload?: string }>(
        '/verifier/presentation-definitions',
        {
          method: 'POST',
          headers: { 'Idempotency-Key': idempotencyKey },
          body: JSON.stringify(body),
        },
      );
      const url = out.url ?? `${window.location.origin}/credentials/respond/${out.id}`;
      setSavedRecord({ id: out.id, url, qrPayload: out.qrPayload ?? `ida://request?pdid=${out.id}` });
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = (key: string, text: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const compiledJson = JSON.stringify(compiled.definition, null, 2);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('proofRequest.title')}</h1>
          <p className="mt-1 text-sm text-text-muted">{t('pex.subtitle')}</p>
        </div>
        <button onClick={() => setShowJson((v) => !v)} className="btn-secondary">
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>code</span>
          {showJson ? t('pex.hideJson') : t('pex.showJson')}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* PD metadata */}
          <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
            <h2 className="mb-4 text-sm font-extrabold uppercase tracking-widest text-text-main">{t('pex.metadata')}</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                label={t('pex.pdName')}
                placeholder="KYC + Diploma proof — Q2 2026"
                value={pdName}
                onChange={(e) => setPdName((e.target as HTMLInputElement).value)}
              />
              <FormField
                label={t('pex.verifierDid')}
                placeholder="did:ida:verifier:..."
                value={verifierDid}
                onChange={(e) => setVerifierDid((e.target as HTMLInputElement).value)}
              />
              <div className="md:col-span-2">
                <FormField
                  as="textarea"
                  label={t('pex.pdPurpose')}
                  placeholder={t('pex.pdPurposePlaceholder')}
                  value={pdPurpose}
                  onChange={(e) => setPdPurpose((e.target as HTMLTextAreaElement).value)}
                />
              </div>
            </div>
          </div>

          {/* Input descriptors */}
          <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-sm font-extrabold uppercase tracking-widest text-text-main">{t('pex.inputDescriptors')}</h2>
              <button onClick={addDescriptor} className="text-xs font-bold text-primary hover:text-primary-dark">
                <span className="material-symbols-outlined align-middle" style={{ fontSize: 14 }}>add</span>
                {t('pex.addDescriptor')}
              </button>
            </div>
            <div className="space-y-4">
              {descriptors.map((d, di) => (
                <div key={di} className="rounded-lg border border-border-subtle bg-slate-50/50 p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-[10px] font-extrabold uppercase tracking-widest text-text-muted">
                      {t('pex.descriptor')} #{di + 1}
                    </span>
                    {descriptors.length > 1 && (
                      <button onClick={() => removeDescriptor(di)} className="text-xs font-bold text-red-500 hover:text-red-600">
                        {t('pex.remove')}
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <FormField label={t('pex.descriptorId')} value={d.id} onChange={(e) => updateDescriptor(di, { id: (e.target as HTMLInputElement).value })} />
                    <FormField label={t('pex.descriptorName')} value={d.name ?? ''} onChange={(e) => updateDescriptor(di, { name: (e.target as HTMLInputElement).value })} />
                    <FormField label={t('pex.descriptorGroup')} hint={t('pex.descriptorGroupHint')} value={d.group ?? ''} onChange={(e) => updateDescriptor(di, { group: (e.target as HTMLInputElement).value })} />
                    <FormField as="select" label={t('pex.schemaPin')} value={d.schemaId ?? ''} onChange={(e) => updateDescriptor(di, { schemaId: (e.target as HTMLSelectElement).value })}>
                      <option value="">{t('pex.schemaPinNone')}</option>
                      {(schemas ?? []).map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name} (v{s.version || '1'})
                        </option>
                      ))}
                    </FormField>
                    <div className="md:col-span-2">
                      <FormField as="textarea" label={t('pex.descriptorPurpose')} value={d.purpose ?? ''} onChange={(e) => updateDescriptor(di, { purpose: (e.target as HTMLTextAreaElement).value })} />
                    </div>
                  </div>

                  {/* Format selector */}
                  <div className="mt-3">
                    <label className="mb-1.5 block text-xs font-bold text-text-muted">{t('pex.formats')}</label>
                    <div className="flex flex-wrap gap-2">
                      {FORMAT_BUCKETS.map((b) => (
                        <button
                          key={b}
                          onClick={() => toggleFormat(di, b)}
                          className={`rounded-lg border px-3 py-1.5 text-xs font-bold transition-all ${
                            d.formats.includes(b)
                              ? 'border-primary bg-blue-50 text-primary'
                              : 'border-border-stroke bg-white text-slate-600 hover:border-slate-400'
                          }`}
                        >
                          {b}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Fields */}
                  <div className="mt-4">
                    <div className="mb-2 flex items-center justify-between">
                      <label className="text-xs font-bold text-text-muted">{t('pex.fields')}</label>
                      <button onClick={() => addField(di)} className="text-xs font-bold text-primary hover:text-primary-dark">
                        <span className="material-symbols-outlined align-middle" style={{ fontSize: 14 }}>add</span>
                        {t('pex.addField')}
                      </button>
                    </div>
                    <div className="space-y-2">
                      {d.fields.map((f, fi) => (
                        <div key={fi} className="rounded border border-border-subtle bg-white p-3">
                          <div className="grid grid-cols-1 gap-2 md:grid-cols-12">
                            <div className="md:col-span-5">
                              <FormField label={t('pex.fieldPath')} value={f.path} onChange={(e) => updateField(di, fi, { path: (e.target as HTMLInputElement).value })} />
                            </div>
                            <div className="md:col-span-3">
                              <FormField as="select" label={t('pex.fieldPredicate')} value={f.predicate ?? ''} onChange={(e) => updateField(di, fi, { predicate: (e.target as HTMLSelectElement).value as BuilderField['predicate'] })}>
                                <option value="">{t('pex.predicateNone')}</option>
                                <option value="required">required</option>
                                <option value="preferred">preferred</option>
                              </FormField>
                            </div>
                            <div className="md:col-span-3 flex items-end gap-2 pb-1">
                              <label className="flex items-center gap-1.5 text-xs font-bold text-text-muted">
                                <input
                                  type="checkbox"
                                  checked={!!f.optional}
                                  onChange={(e) => updateField(di, fi, { optional: e.target.checked })}
                                />
                                {t('pex.fieldOptional')}
                              </label>
                            </div>
                            <div className="md:col-span-1 flex items-end pb-1">
                              {d.fields.length > 1 && (
                                <button onClick={() => removeField(di, fi)} className="text-text-faint hover:text-red-500">
                                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>delete</span>
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="mt-2">
                            <FormField
                              label={t('pex.fieldFilter')}
                              hint={t('pex.fieldFilterHint')}
                              placeholder='{"type":"string","minLength":1}'
                              value={f.filter ?? ''}
                              onChange={(e) => updateField(di, fi, { filter: (e.target as HTMLInputElement).value })}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Submission requirements */}
          <details className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
            <summary className="cursor-pointer text-sm font-extrabold uppercase tracking-widest text-text-main">
              {t('pex.submissionRequirements')}
              <span className="ml-2 text-xs font-normal text-text-faint">{t('pex.advanced')}</span>
            </summary>
            <div className="mt-4 space-y-3">
              {srs.map((sr, si) => (
                <div key={si} className="rounded-lg border border-border-subtle bg-slate-50/50 p-3">
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-12">
                    <div className="md:col-span-3">
                      <FormField label={t('pex.srName')} value={sr.name ?? ''} onChange={(e) => updateSR(si, { name: (e.target as HTMLInputElement).value })} />
                    </div>
                    <div className="md:col-span-2">
                      <FormField as="select" label={t('pex.srRule')} value={sr.rule} onChange={(e) => updateSR(si, { rule: (e.target as HTMLSelectElement).value as 'all' | 'pick' })}>
                        <option value="pick">pick</option>
                        <option value="all">all</option>
                      </FormField>
                    </div>
                    <div className="md:col-span-2">
                      <FormField label={t('pex.srFrom')} value={sr.from ?? ''} onChange={(e) => updateSR(si, { from: (e.target as HTMLInputElement).value })} />
                    </div>
                    {sr.rule === 'pick' && (
                      <>
                        <div className="md:col-span-1">
                          <FormField label="count" type="number" value={sr.count ?? ''} onChange={(e) => updateSR(si, { count: (e.target as HTMLInputElement).value })} />
                        </div>
                        <div className="md:col-span-1">
                          <FormField label="min" type="number" value={sr.min ?? ''} onChange={(e) => updateSR(si, { min: (e.target as HTMLInputElement).value })} />
                        </div>
                        <div className="md:col-span-1">
                          <FormField label="max" type="number" value={sr.max ?? ''} onChange={(e) => updateSR(si, { max: (e.target as HTMLInputElement).value })} />
                        </div>
                      </>
                    )}
                    <div className="md:col-span-1 flex items-end pb-1">
                      <button onClick={() => removeSR(si)} className="text-text-faint hover:text-red-500">
                        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              <button onClick={addSR} className="text-xs font-bold text-primary hover:text-primary-dark">
                <span className="material-symbols-outlined align-middle" style={{ fontSize: 14 }}>add</span>
                {t('pex.addSR')}
              </button>
            </div>
          </details>

          {/* Compiler warnings */}
          {compiled.warnings.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              {compiled.warnings.map((w, i) => (
                <div key={i}>{w}</div>
              ))}
            </div>
          )}

          {/* Save action */}
          <div className="flex items-center gap-3">
            <button onClick={handleSave} disabled={saving || descriptors.length === 0} className="btn-primary">
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>save</span>
              {saving ? t('pex.saving') : t('pex.save')}
            </button>
            {saveError && <span className="text-xs font-bold text-red-500">{saveError}</span>}
          </div>
        </div>

        {/* Right column: JSON preview / share */}
        <div className="space-y-6">
          {showJson && (
            <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-sm font-extrabold uppercase tracking-widest text-text-main">{t('pex.jsonPreview')}</h2>
                <button onClick={() => handleCopy('json', compiledJson)} className="text-xs font-bold text-primary hover:text-primary-dark">
                  {copied === 'json' ? t('pex.copied') : t('pex.copy')}
                </button>
              </div>
              <pre className="max-h-[480px] overflow-auto rounded-lg border border-border-subtle bg-slate-50 p-4 font-mono text-[10px] text-text-main">
                {compiledJson}
              </pre>
            </div>
          )}

          {savedRecord ? (
            <>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 shadow-sm">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-emerald-600" style={{ fontSize: 18 }}>check_circle</span>
                  <span className="text-sm font-bold text-emerald-700">{t('pex.savedTitle')}</span>
                </div>
                <p className="mt-1 font-mono text-[10px] text-emerald-800">{savedRecord.id}</p>
              </div>

              <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-sm font-extrabold uppercase tracking-widest text-text-main">{t('proofRequest.qrCode')}</h2>
                <div className="flex justify-center">
                  <QRCode value={savedRecord.qrPayload ?? savedRecord.url} size={200} />
                </div>
                <p className="mt-3 text-center text-[10px] text-text-faint">{t('proofRequest.scanHint')}</p>
              </div>

              <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
                <h2 className="mb-3 text-sm font-extrabold uppercase tracking-widest text-text-main">{t('proofRequest.shareableLink')}</h2>
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={savedRecord.url}
                    className="flex-1 rounded-lg border border-border-subtle bg-slate-50 px-3 py-2 font-mono text-xs text-text-main"
                  />
                  <button
                    onClick={() => handleCopy('url', savedRecord.url)}
                    className="flex size-9 items-center justify-center rounded-lg border border-border-stroke transition-colors hover:bg-slate-50"
                  >
                    <span className="material-symbols-outlined text-text-faint" style={{ fontSize: 18 }}>
                      {copied === 'url' ? 'check' : 'content_copy'}
                    </span>
                  </button>
                </div>
              </div>
            </>
          ) : (
            !showJson && (
              <div className="flex h-64 items-center justify-center rounded-xl border border-border-subtle bg-white p-6 text-center shadow-sm">
                <div>
                  <span className="material-symbols-outlined mb-2 text-text-faint" style={{ fontSize: 40 }}>qr_code_2</span>
                  <p className="text-sm text-text-faint">{t('pex.saveHint')}</p>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
